import { Link } from "react-router-dom";
import BackToDocumentary from "../../shared/BackToDocumentary/BackToDocumentary";
import type { NonCredlyCert } from "../../../utils/nonCredlyCerts";
import type { Project } from "../../../types/content";
import { aiAchievements, aiCredentials, aiProjects, aiService, aiSkills, aiStory, aiWorkshop } from "./aiItems";
import type { AiAchievement } from "./aiItems";
import { productBrand } from "../../../utils/productName";

/**
 * ============================================================================
 * AI & Innovation — the /ai-innovation route.
 * ============================================================================
 *
 * A CONSOLIDATION, NOT A CLAIM. Every item on this page is already published
 * somewhere else on this site, and every string it prints is the content
 * layer's own — a credential's name and issuer, a project's description, a
 * story's summary, a workshop's description, the skills taxonomy's own entries.
 * Nothing here is authored about the career. The page's own prose describes the
 * PAGE, which is a thing a reader can check against it.
 *
 * That constraint is the reason the route exists at all. The owner's AI work is
 * filed correctly and separately — a credential stack in Certifications, a
 * framework in Experience and again in Career Stories, builds in Projects, a
 * session in Workshops, a discipline in Skills and a line in Services — and no
 * one of those places shows the shape of the whole. A hub that links to all of
 * them is the one thing that was missing, and it is the only thing this page
 * adds.
 *
 * WHAT COUNTS AS "THE AI WORK" IS DECIDED IN `aiItems.ts`, NOT HERE (D-095).
 * That selection now has four consumers — this page, the Home teaser,
 * `routeMeta` and the dashboard figure registry — and four copies of a rule is
 * four things that drift. See that file for which items are derived from a
 * field the layer carries and which are named by id.
 *
 * ONE CREDENTIAL IS DELIBERATELY ABSENT AND ITS ABSENCE IS STRUCTURAL.
 * `certifications.json` carries `ibm-ai-healthcare` ("AI in Healthcare") at
 * `status: "pending-details"` — completed, but with no issuer verification URL.
 * The publish gate bars it and this page never sees it, because the list below
 * comes from the rendered card set rather than from the JSON. That is the right
 * direction of travel: a page that filtered `certifications.json` by category
 * would have had to remember the gate.
 */

/**
 * The page's standfirst — used verbatim as this route's meta description
 * (D-044).
 *
 * It says what the page IS and makes no assertion about the work, for D-066's
 * reason: a standfirst ships in the `<meta name="description">` and is the one
 * line on a page that nothing checks. Counts belong to the layer, and
 * `routeMeta` computes them from it.
 */
export const AI_INNOVATION_STANDFIRST =
	"The AI work already recorded across this site, gathered into one view — the credentials, the framework built into delivery, the platforms shipped, and the session run for the organisation. Each item links to where it is documented in full.";

/* -------------------------------------------------------------------------- */

const Panel = ({
	id,
	title,
	blurb,
	children
}: {
	id: string;
	title: string;
	blurb: string;
	children: React.ReactNode;
}): JSX.Element => (
	<section className="mt-14" aria-labelledby={id}>
		{/* `scroll-mt-24` because these ids are now LINK TARGETS (D-100): the Home
		   strip's cards each land on the panel that holds their detail, and the
		   fixed navbar would otherwise cover the heading a reader was sent to.
		   The id stays on the heading rather than moving to the section, because
		   `aria-labelledby` above already points at it and one id doing one job
		   in two places is worse than the offset. */}
		<h2 id={id} className="scroll-mt-24 text-xl font-semibold text-[var(--text-strong)] sm:text-2xl">
			{title}
		</h2>
		<p className="mt-2 max-w-[70ch] text-sm leading-relaxed text-[var(--text-muted)]">{blurb}</p>
		<div className="mt-6">{children}</div>
	</section>
);

/** The link that says where an item actually lives. Every card carries one. */
const HomeLink = ({ to, children }: { to: string; children: React.ReactNode }): JSX.Element => (
	<Link
		to={to}
		className="mt-4 inline-block min-h-[24px] text-xs font-medium text-[var(--accent-link)] transition-colors duration-200 hover:text-[var(--accent-link-hover)]"
	>
		{children}
	</Link>
);

export default function AiInnovation(): JSX.Element {
	return (
		<section className="section-shell" aria-labelledby="ai-innovation-heading">
			<div className="section-rail">
				<p className="section-eyebrow">Consolidated View</p>

				{/* THE ROUTE'S ONLY <h1>. Every route ships exactly one (D-042). */}
				<h1 id="ai-innovation-heading" className="heading">
					<span className="bg-gradient-to-r from-[var(--grad-brand-from)] via-[var(--grad-brand-via)] to-[var(--grad-brand-to)] bg-clip-text text-transparent">
						AI
					</span>{" "}
					&amp; Innovation
				</h1>

				<p className="section-standfirst">{AI_INNOVATION_STANDFIRST}</p>

				{/* ------------------------------------------------- credentials -- */}
				<Panel
					id="ai-credentials"
					title="The credential stack"
					blurb="Every credential the Certifications section files under AI, newest first. Names, issuers and years are the layer's; the cards themselves, with their scans and issuer links, are on the home page."
				>
					<ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
						{aiCredentials.map((cert: NonCredlyCert) => (
							<li key={cert.id} className="card-shell">
								<div className="card-face flex h-full flex-col p-5">
									<h3 className="text-sm font-semibold leading-snug text-[var(--text-strong)]">
										{cert.title}
									</h3>
									<p className="mt-2 text-xs text-[var(--text-secondary)]">{cert.issuer}</p>
									<p className="mt-auto pt-3 text-xs tabular-nums text-[var(--text-muted)]">
										{cert.year}
									</p>
								</div>
							</li>
						))}
					</ul>
					<HomeLink to="/#certifications">See these cards in Certifications →</HomeLink>
				</Panel>

				{/* --------------------------------------------------- delivery -- */}
				<Panel
					id="ai-delivery"
					title="Applied in delivery"
					blurb="Where the tooling is used on the job rather than studied. Every line below is quoted from the current role's own record."
				>
					<ul className="space-y-4">
						{aiAchievements.map((achievement: AiAchievement) => (
							/* A LINE WITH A PRODUCT BEHIND IT IS A LINK; ONE WITHOUT IS NOT,
							   and the two shapes in one list are the honest arrangement
							   rather than an inconsistency. `products.json` records the BGV
							   and screening integrations among SFT Evolve's own outcomes, so
							   that card has a destination the layer can substantiate. The
							   compliance framework is a framework, not a product, and its
							   account is the Career Stories card below — inventing a
							   plausible destination for it is exactly what D-102 declined to
							   do for the dashboard metrics.

							   `after:inset-0` ON THE ANCHOR, not an onClick on the card. It
							   is the pattern every other clickable card on this site uses
							   (Workshops, Products, Projects, Recognition): one tab stop, one
							   accessible name, and a real href a reader can copy, where a
							   click handler is invisible to the keyboard and to a crawler.
							   The pointer follows from the anchor covering the card;
							   `cursor-pointer` on the shell states it for the 1px gradient
							   border the pseudo-element does not reach.

							   IT IS NOT `HomeLink` deliberately — that component is the plain
							   "where this lives" link used six times on this page, and giving
							   it a stretch prop would put an invisible full-card overlay one
							   argument away from every one of them. */
							<li
								key={achievement.text}
								className={
									achievement.product ? "card-shell group relative cursor-pointer" : "card-shell"
								}
							>
								<div className="card-face p-5">
									<p className="text-sm leading-relaxed text-[var(--text-secondary)]">
										{achievement.text}
									</p>
									{achievement.product && (
										<Link
											to={`/products#${achievement.product.id}`}
											className="mt-4 inline-block min-h-[24px] rounded-[4px] text-xs font-medium text-[var(--accent-link)] transition-colors duration-200 after:absolute after:inset-0 after:rounded-[1rem] after:content-[''] hover:text-[var(--accent-link-hover)] focus:outline-none focus-visible:ring-4 focus-visible:ring-[var(--ring-accent)]"
										>
											The product these integrations shipped in —{" "}
											{productBrand(achievement.product.name)} →
										</Link>
									)}
								</div>
							</li>
						))}
					</ul>

					<div className="card-shell mt-6">
						<div className="card-face p-5">
							<h3 className="text-base font-semibold text-[var(--text-strong)]">{aiStory.title}</h3>
							<p className="mt-1 text-xs text-[var(--text-muted)]">
								{aiStory.organization} · {aiStory.period}
							</p>
							<p className="mt-3 text-sm leading-relaxed text-[var(--text-secondary)]">
								{aiStory.summary}
							</p>
							<div className="mt-3 flex flex-wrap gap-2">
								{aiStory.tags.map((tag: string) => (
									<span
										key={tag}
										className="pill-tint border-[var(--cat-indigo-border)] bg-[var(--cat-indigo-fill)] text-[var(--cat-indigo-text)]"
									>
										{tag}
									</span>
								))}
							</div>
							<HomeLink to="/stories">Read the full account in Career Stories →</HomeLink>
						</div>
					</div>

					<HomeLink to="/#experience">The role these lines belong to →</HomeLink>
				</Panel>

				{/* ---------------------------------------------------- projects -- */}
				<Panel
					id="ai-built"
					title="Built"
					blurb="The platforms the Projects page files under AI innovation. Descriptions and outcomes are the layer's own."
				>
					<ul className="grid grid-cols-1 gap-5 lg:grid-cols-2">
						{aiProjects.map((project: Project) => (
							<li key={project.id} className="card-shell">
								<div className="card-face flex h-full flex-col p-5">
									<h3 className="text-base font-semibold text-[var(--text-strong)]">
										{project.name}
									</h3>
									<p className="mt-1 text-xs text-[var(--text-muted)]">
										{project.organization} · {project.role}
									</p>
									<p className="mt-3 text-sm leading-relaxed text-[var(--text-secondary)]">
										{project.description}
									</p>
									<ul className="mt-3 space-y-1">
										{project.outcomes.map((outcome: string) => (
											<li
												key={outcome}
												className="text-xs leading-relaxed text-[var(--text-muted)]"
											>
												— {outcome}
											</li>
										))}
									</ul>
									<div className="mt-3 flex flex-wrap gap-2">
										{project.tags.map((tag: string) => (
											<span
												key={tag}
												className="pill-tint border-[var(--cat-teal-border)] bg-[var(--cat-teal-fill)] text-[var(--cat-teal-text)]"
											>
												{tag}
											</span>
										))}
									</div>
								</div>
							</li>
						))}
					</ul>
					<HomeLink to="/projects">In Projects, beside the rest of the portfolio →</HomeLink>
				</Panel>

				{/* ---------------------------------------------------- workshop -- */}
				<Panel
					id="ai-taught"
					title="Taught"
					blurb="The session run for the organisation on adopting the tooling. It has its own page, with the photographs from the day."
				>
					<div className="card-shell">
						<div className="card-face p-5">
							<h3 className="text-base font-semibold text-[var(--text-strong)]">{aiWorkshop.title}</h3>
							<p className="mt-1 text-xs text-[var(--text-muted)]">
								{aiWorkshop.role} · {aiWorkshop.host}
							</p>
							<p className="mt-3 text-sm leading-relaxed text-[var(--text-secondary)]">
								{aiWorkshop.description}
							</p>
							<HomeLink to={`/workshops/${aiWorkshop.slug}`}>Open the session page →</HomeLink>
						</div>
					</div>
				</Panel>

				{/* ------------------------------------------------------ skills -- */}
				<Panel
					id="ai-capability"
					title="The capability, as the layer records it"
					blurb="The skills group and the service line that name this work elsewhere on the site, quoted rather than restated."
				>
					<div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
						<div className="card-shell">
							<div className="card-face flex h-full flex-col p-5">
								<h3 className="text-base font-semibold text-[var(--text-strong)]">{aiSkills.name}</h3>
								<ul className="mt-3 flex flex-wrap gap-2">
									{aiSkills.skills.map((skill: string) => (
										<li
											key={skill}
											className="pill-tint border-[var(--cat-violet-border)] bg-[var(--cat-violet-fill)] text-[var(--cat-violet-text)]"
										>
											{skill}
										</li>
									))}
								</ul>
								<HomeLink to="/#skills">In Skills →</HomeLink>
							</div>
						</div>

						<div className="card-shell">
							<div className="card-face flex h-full flex-col p-5">
								<h3 className="text-base font-semibold text-[var(--text-strong)]">{aiService.title}</h3>
								<p className="mt-3 text-sm leading-relaxed text-[var(--text-secondary)]">
									{aiService.description}
								</p>
								<HomeLink to="/#services">In Services →</HomeLink>
							</div>
						</div>
					</div>
				</Panel>

				<div className="mt-16 flex flex-wrap justify-center gap-6">
					<Link
						to="/dashboard"
						className="inline-block min-h-[24px] text-sm font-medium text-[var(--accent-link)] transition-colors duration-200 hover:text-[var(--accent-link-hover)]"
					>
						Career Dashboard →
					</Link>
					<BackToDocumentary />
				</div>
			</div>
		</section>
	);
}
